// Deterministic comparison rules for two listings. No randomness, no network, no AI.
export const ZONES = [
  { key: "engine", label: "Engine and gearbox", weight: 3, physical: true },
  { key: "tyres", label: "Tyres", weight: 1, physical: true },
  { key: "brakes", label: "Brakes", weight: 2, physical: true },
  { key: "suspension", label: "Suspension", weight: 2, physical: true },
  { key: "body", label: "Bodywork", weight: 2, physical: true },
  { key: "running", label: "Running costs", weight: 1, physical: true },
  { key: "price", label: "Price", weight: 2, physical: false },
  { key: "history", label: "Mileage history", weight: 2, physical: true },
];
export const STATUS_LABEL = { good: "Stronger", amber: "Worth checking", red: "Higher risk", grey: "No data" };
const RANK = { good: 2, amber: 1, red: 0 };
const SCORE = { good: 1, amber: 0.5, red: 0 };
const CONDITION_RANK = { Excellent: 3, Good: 2, Fair: 1, "Needs work": 0 };
const KEYWORDS = {
  tyres: ["tyre", "tread"],
  brakes: ["brake", "disc", "pad", "caliper"],
  suspension: ["suspension", "shock", "spring", "steering", "track rod", "ball joint", "bush"],
};
const CORROSION = ["corrosion", "rust", "sill"];
const NAMES = ["Car A", "Car B"];
const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const fmt = (n) => Math.round(n).toLocaleString("en-GB");
const worst = (...s) => s.reduce((w, x) => (RANK[x] < RANK[w] ? x : w), "good");

function engineRule(a, b) {
  const out = ["good", "good"];
  const ev = [];
  const ma = num(a.mileage), mb = num(b.mileage), ya = num(a.year), yb = num(b.year);
  if (ma === null || mb === null || ya === null || yb === null) {
    return { status: ["grey", "grey"], reason: "Mileage or year is missing, so the engine and gearbox can't be compared.", evidence: [] };
  }
  const md = Math.abs(ma - mb), yd = Math.abs(ya - yb);
  const hi = ma > mb ? 0 : 1, old = ya < yb ? 0 : 1;
  const mSev = md >= 25000 ? "red" : md >= 10000 ? "amber" : "good";
  const ySev = yd >= 3 ? "red" : yd >= 1 ? "amber" : "good";
  if (md > 0) { out[hi] = worst(out[hi], mSev); ev.push(`${NAMES[hi]} has ${fmt(md)} more miles.`); }
  if (yd > 0) { out[old] = worst(out[old], ySev); ev.push(`${NAMES[old]} is ${yd} year${yd > 1 ? "s" : ""} older.`); }
  const flagged = out.map((s, i) => (s !== "good" ? NAMES[i] : null)).filter(Boolean);
  const reason = flagged.length
    ? `${flagged.join(" and ")} ${flagged.length > 1 ? "have" : "has"} done more work, so the engine and gearbox carry ${out.includes("red") ? "a higher risk" : "more wear worth checking"}.`
    : "Both cars have similar age and mileage, so engine and gearbox wear should be similar.";
  return { status: out, reason, evidence: ev };
}

function defectRule(key, label) {
  return (a, b) => {
    const status = [a, b].map((car) => {
      if (!Array.isArray(car.mot_defects)) return "grey";
      const hits = car.mot_defects.filter((d) => KEYWORDS[key].some((k) => (d.text || "").toLowerCase().includes(k)));
      if (!hits.length) return "good";
      return hits.every((d) => ["advisory", "minor"].includes((d.type || "").toLowerCase())) ? "amber" : "red";
    });
    const evidence = [a, b].flatMap((car, i) => (Array.isArray(car.mot_defects) ? car.mot_defects : [])
      .filter((d) => KEYWORDS[key].some((k) => (d.text || "").toLowerCase().includes(k)))
      .map((d) => `${NAMES[i]} MOT ${d.type || "note"}: ${d.text}`));
    const reason = status.every((s) => s === "grey")
      ? `No MOT history is available yet, so ${label.toLowerCase()} can't be judged.`
      : evidence.length ? `The latest MOT notes ${label.toLowerCase()} items worth looking at.` : `No ${label.toLowerCase()} issues on the latest MOT.`;
    return { status, reason, evidence };
  };
}

function bodyRule(a, b) {
  const ra = CONDITION_RANK[a.condition], rb = CONDITION_RANK[b.condition];
  let status = ["grey", "grey"];
  const evidence = [];
  if (ra !== undefined && rb !== undefined) {
    status = ["good", "good"];
    const d = Math.abs(ra - rb), w = ra < rb ? 0 : 1;
    if (d >= 2) status[w] = "red"; else if (d === 1) status[w] = "amber";
    evidence.push(`Seller says: Car A ${a.condition}, Car B ${b.condition}.`);
  }
  [a, b].forEach((car, i) => {
    const rust = (car.mot_defects || []).find((x) => CORROSION.some((k) => (x.text || "").toLowerCase().includes(k)));
    if (rust) { status[i] = "red"; evidence.push(`${NAMES[i]} MOT: ${rust.text}`); }
  });
  const reason = status.every((s) => s === "grey") ? "Condition wasn't given for both cars." : status.includes("red") || status.includes("amber")
    ? `${NAMES[status.indexOf(status.includes("red") ? "red" : "amber")]} is described in worse condition, so its bodywork is ${status.includes("red") ? "a higher risk" : "worth checking"}.`
    : "Both cars are described in similar condition.";
  return { status, reason, evidence };
}

function runningRule(a, b) {
  const ea = /electric/i.test(a.fuel || ""), eb = /electric/i.test(b.fuel || "");
  if (ea !== eb) {
    const status = ea ? ["good", "amber"] : ["amber", "good"];
    return { status, reason: `${NAMES[ea ? 0 : 1]} is electric, which usually costs less to run.`, evidence: [`Car A: ${a.fuel || "?"}, Car B: ${b.fuel || "?"}`] };
  }
  if (ea && eb) return { status: ["good", "good"], reason: "Both cars are electric.", evidence: [] };
  const ca = num(a.engine_cc), cb = num(b.engine_cc);
  if (ca === null || cb === null) return { status: ["grey", "grey"], reason: "Engine size is missing, so running costs can't be compared.", evidence: [] };
  const status = ["good", "good"];
  const d = Math.abs(ca - cb);
  if (d >= 800) status[ca > cb ? 0 : 1] = "amber";
  return {
    status,
    reason: d >= 800 ? `${NAMES[ca > cb ? 0 : 1]} has a much bigger engine, so fuel, tax and insurance are likely higher.` : "Engine sizes are close, so running costs should be similar.",
    evidence: [`Car A: ${fmt(ca)} cc, Car B: ${fmt(cb)} cc`],
  };
}

function priceRule(a, b) {
  const pa = num(a.price), pb = num(b.price);
  if (pa === null || pb === null || !pa || !pb) return { status: ["grey", "grey"], reason: "Price is missing for one car.", evidence: [] };
  const status = ["good", "good"];
  if (pa === pb) return { status, reason: "Both cars are the same price.", evidence: [] };
  const dear = pa > pb ? 0 : 1, cheap = 1 - dear;
  const [D, C] = dear === 0 ? [a, b] : [b, a];
  const pct = (Math.max(pa, pb) / Math.min(pa, pb) - 1) * 100;
  const justified = num(D.year) > num(C.year) && num(D.mileage) < num(C.mileage);
  if (!justified) status[dear] = pct >= 15 ? "red" : pct >= 5 ? "amber" : "good";
  const reason = justified
    ? `${NAMES[dear]} costs ${pct.toFixed(0)}% more but is newer with fewer miles, so the price looks fair.`
    : status[dear] === "good" ? "Prices are within 5% of each other." : `${NAMES[dear]} costs ${pct.toFixed(0)}% more than ${NAMES[cheap]} without being newer and lower-mileage.`;
  return { status, reason, evidence: [`Car A £${fmt(pa)}, Car B £${fmt(pb)}`] };
}

function historyRule(a, b, now) {
  const evidence = [];
  const status = [a, b].map((car, i) => {
    const hist = Array.isArray(car.mot_history) ? [...car.mot_history].sort((x, y) => String(x.date).localeCompare(String(y.date))) : [];
    const readings = hist.map((h) => num(h.mileage)).filter((m) => m !== null);
    if (num(car.lookup_last_mileage) !== null && !hist.length) readings.push(num(car.lookup_last_mileage));
    if (!readings.length) return "grey";
    const drop = readings.some((m, j) => j > 0 && m < readings[j - 1]);
    const last = readings[readings.length - 1];
    if (drop || num(car.mileage) < last) { evidence.push(`${NAMES[i]}: listed mileage or MOT readings go backwards (last MOT ${fmt(last)}).`); return "red"; }
    const perYear = num(car.mileage) / Math.max(1, now - num(car.year));
    if (perYear > 40000) { evidence.push(`${NAMES[i]}: about ${fmt(perYear)} miles a year.`); return "amber"; }
    return "good";
  });
  const reason = status.every((s) => s === "grey") ? "No MOT mileage history is available yet." : status.includes("red")
    ? "A mileage reading goes backwards, which is a higher risk and worth checking with the seller." : status.includes("amber") ? "Very high yearly mileage is worth checking." : "Mileage history looks consistent.";
  return { status, reason, evidence };
}

const RULES = {
  engine: engineRule, tyres: defectRule("tyres", "Tyres"), brakes: defectRule("brakes", "Brakes"),
  suspension: defectRule("suspension", "Suspension"), body: bodyRule, running: runningRule, price: priceRule, history: historyRule,
};

export function compareCars(a, b, now = new Date().getFullYear()) {
  const zones = {};
  ZONES.forEach((z) => { zones[z.key] = RULES[z.key](a, b, now); });
  const scores = [0, 1].map((i) => {
    let w = 0, s = 0;
    ZONES.forEach((z) => { const st = zones[z.key].status[i]; if (st !== "grey") { w += z.weight; s += z.weight * SCORE[st]; } });
    return w ? Math.round((s / w) * 100) : null;
  });
  const better = scores[0] === null || scores[1] === null || scores[0] === scores[1] ? null : scores[0] > scores[1] ? 0 : 1;
  const notes = [0, 1].map((i) => ({
    positives: ZONES.filter((z) => { const m = zones[z.key].status[i], o = zones[z.key].status[1 - i]; return m !== "grey" && o !== "grey" && RANK[m] > RANK[o]; }).map((z) => `Stronger on ${z.label.toLowerCase()}`),
    watch: ZONES.filter((z) => ["red", "amber"].includes(zones[z.key].status[i])).map((z) => `${z.label}: ${zones[z.key].status[i] === "red" ? "higher risk" : "worth checking"}`),
  }));
  return { zones, scores, better, notes };
}
